import { PARTS } from '@/data/parts'
import type { FramePoints } from '@/utils/geometry'
import type { FrameAnchorId, MmPoint, PartCategoryId } from '@/types'

/**
 * The connection system: every part type is characterised against the bike's
 * side view by one of three geometric methods, matching how the part really
 * mounts:
 *
 * - `two-point`   — the part bolts to the bike at two known points (fork:
 *                   axle + crown; shock: both eyes). Two dots on the image →
 *                   position, rotation and scale are all solved.
 * - `point-radius`— round parts (wheels, rotors) with a visible centre. One dot
 *                   on the hub / centre plus a circle handle dragged to the edge.
 *                   Scale comes from the real radius — wheels take it from the
 *                   bike's geometry, rotors from the entered diameter.
 * - `three-edge`  — round parts with NO visible centre to click (bare tyres).
 *                   Three dots anywhere on the rim; the circle through them
 *                   (circumcircle) gives the centre — which snaps to the axle —
 *                   and the radius, so the dots need not be diametrically
 *                   opposite. Scale comes from the real radius, same as
 *                   point-radius. Only the first dot carries a `bikeAnchor`. An
 *                   optional fourth dot on the inner (rim-seat) edge records the
 *                   tyre's inner-radius ratio, which the rim scales its outer edge
 *                   to so the two meet.
 * - `point-length`— parts that mount at one point with a known real dimension
 *                   (cranks: spindle + arm-length line; saddle: rails + nose-
 *                   to-tail line). The drawn line sets scale and orientation;
 *                   the bike-side angle is configurable.
 */

export type AnchorSchemeKind = 'two-point' | 'point-radius' | 'three-edge' | 'point-length'

export interface AnchorPointSpec {
  /** Key the dot is stored under in `imageAnchors`. */
  id: string
  /** Short label shown next to the dot in the editor. */
  label: string
  /** One-line instruction for where to click on the image. */
  hint: string
  /** Bike-side anchor this dot snaps to. Absent for radius handles / length references. */
  bikeAnchor?: string
  /**
   * Dots the part can be placed without. The part solves and previews from its
   * required dots alone; an optional dot only refines a secondary measurement
   * (e.g. the tyre's inner rim line, which sizes the rim but not the tyre).
   */
  optional?: boolean
}

export interface AnchorScheme {
  kind: AnchorSchemeKind
  /** The mount dot first, then the second dot / handle / reference. `three-edge` adds a third rim dot. */
  points: AnchorPointSpec[]
  /** Render the part a second time at the rear-wheel equivalents (wheels, tyres, rotors). */
  mirror?: boolean
  /** point-radius: where the real radius comes from. */
  radiusSource?: 'wheel' | 'value'
  /**
   * three-edge: id of the optional dot placed on the inner (rim-seat) edge of the
   * tyre. Together with the outer circle it yields the tyre's inner-radius ratio,
   * which the rim then scales its outer edge to so the two meet.
   */
  innerRadiusPointId?: string
  /** Real mm the drawn line (or entered diameter) represents, before the admin overrides it. */
  defaultLengthMm?: number
  /** Label for the mm input in the workbench. */
  lengthLabel?: string
  /** point-length: bike-side angle of the drawn line in degrees — 0 = level toward the front wheel, positive tips it up. */
  defaultAngleDeg?: number
  /** point-length: derive the target line angle from the current frame geometry. */
  angleSource?: 'actual-seat-tube'
  /** Layer order in the composed preview. */
  zIndex: number
}

/**
 * Bike-side anchors that geometry alone can't derive — the suspension mounts.
 * Everything else (bar clamp, saddle, pedal axle, calipers) is computed from
 * the geometry, so the only points the admin positions by hand are the shock
 * eyes, which depend on the linkage. Each has a sensible default from the
 * skeleton; the admin drags them into place per bike on the Geometry tab.
 */
export interface CustomAnchorDef {
  id: string
  label: string
  hint: string
  defaultPoint: (p: FramePoints) => MmPoint
}

export const CUSTOM_ANCHOR_DEFS: CustomAnchorDef[] = [
  {
    id: 'shock-front',
    label: 'Shock front eye',
    hint: 'Where the front shock mount sits on the frame.',
    defaultPoint: (p) => ({ xMm: p.headTubeTop.x * 0.38, yMm: p.headTubeTop.y * 0.55 }),
  },
  {
    id: 'shock-rear',
    label: 'Shock rear eye',
    hint: 'Where the rocker / rear shock mount sits.',
    defaultPoint: (p) => ({ xMm: p.bb.x + 20, yMm: p.headTubeTop.y * 0.42 }),
  },
]

/** Anchors computed directly from the geometry — shown locked on the workbench. */
export interface DerivedAnchorDef {
  id: string
  label: string
  point: (p: FramePoints) => MmPoint
}

export const DERIVED_ANCHOR_DEFS: DerivedAnchorDef[] = [
  { id: 'rearAxle', label: 'Rear axle', point: (p) => ({ xMm: p.rearAxle.x, yMm: p.rearAxle.y }) },
  { id: 'frontAxle', label: 'Front axle', point: (p) => ({ xMm: p.frontAxle.x, yMm: p.frontAxle.y }) },
  { id: 'bb', label: 'Bottom bracket', point: (p) => ({ xMm: p.bb.x, yMm: p.bb.y }) },
  { id: 'headTubeTop', label: 'Head tube top', point: (p) => ({ xMm: p.headTubeTop.x, yMm: p.headTubeTop.y }) },
  { id: 'bar-clamp', label: 'Bar clamp', point: (p) => ({ xMm: p.barClamp.x, yMm: p.barClamp.y }) },
  { id: 'saddle', label: 'Saddle', point: (p) => ({ xMm: p.saddle.x, yMm: p.saddle.y }) },
  { id: 'crank-end', label: 'Pedal axle', point: (p) => ({ xMm: p.crankEnd.x, yMm: p.crankEnd.y }) },
]

const FRAME_ANCHOR_IDS: FrameAnchorId[] = [
  'bb', 'rearAxle', 'frontAxle', 'headTubeTop', 'headTubeBot', 'seatTubeTop', 'frontTyreTop', 'rearTyreTop',
]

export function isFrameAnchor(id: string): id is FrameAnchorId {
  return (FRAME_ANCHOR_IDS as string[]).includes(id)
}

/**
 * Resolve any bike-side anchor id to a point in frame mm space. Frame hardpoints
 * and geometry-derived mounts come straight from the skeleton; only the shock
 * eyes fall back to the admin's per-bike positions.
 */
export function resolveBikeAnchor(
  id: string,
  points: FramePoints,
  customAnchors: Record<string, MmPoint> | undefined,
): MmPoint {
  switch (id) {
    case 'bb':          return { xMm: points.bb.x, yMm: points.bb.y }
    case 'rearAxle':    return { xMm: points.rearAxle.x, yMm: points.rearAxle.y }
    case 'frontAxle':   return { xMm: points.frontAxle.x, yMm: points.frontAxle.y }
    case 'headTubeTop': return { xMm: points.headTubeTop.x, yMm: points.headTubeTop.y }
    case 'headTubeBot': return { xMm: points.headTubeBot.x, yMm: points.headTubeBot.y }
    case 'seatTubeTop': return { xMm: points.seatTubeTop.x, yMm: points.seatTubeTop.y }
    case 'frontTyreTop': return { xMm: points.frontAxle.x, yMm: points.frontAxle.y + points.frontRadius }
    case 'rearTyreTop':  return { xMm: points.rearAxle.x, yMm: points.rearAxle.y + points.rearRadius }
    case 'bar-clamp':    return { xMm: points.barClamp.x, yMm: points.barClamp.y }
    case 'saddle':       return { xMm: points.saddle.x, yMm: points.saddle.y }
    case 'crank-end':    return { xMm: points.crankEnd.x, yMm: points.crankEnd.y }
    case 'front-caliper': return { xMm: points.frontCaliper.x, yMm: points.frontCaliper.y }
    case 'rear-caliper':  return { xMm: points.rearCaliper.x, yMm: points.rearCaliper.y }
    default: {
      const custom = customAnchors?.[id]
      if (custom) return custom
      const def = CUSTOM_ANCHOR_DEFS.find((anchor) => anchor.id === id)
      return def ? def.defaultPoint(points) : { xMm: points.bb.x, yMm: points.bb.y }
    }
  }
}

/** Front-side anchor → rear-side anchor for mirrored schemes. */
export const MIRROR_ANCHOR_MAP: Record<string, string> = {
  frontAxle: 'rearAxle',
  frontTyreTop: 'rearTyreTop',
  'front-caliper': 'rear-caliper',
}

const SCHEMES: Record<string, AnchorScheme> = {
  // ── Structure ──────────────────────────────────────────────────────────────
  frame: {
    kind: 'two-point',
    zIndex: 20,
    points: [
      { id: 'rear-axle', label: 'Rear axle', hint: 'Centre of the rear axle / dropout — snaps to the geometry rear axle.', bikeAnchor: 'rearAxle' },
      { id: 'bb', label: 'Bottom bracket', hint: 'Centre of the BB shell — snaps to the geometry BB.', bikeAnchor: 'bb' },
    ],
  },
  // The flat side render captured from the 3D finish configurator. Anchored on
  // pose-exact CAD features computed by scripts/build-frame-model.mjs.
  'frame/render': {
    kind: 'two-point',
    zIndex: 20,
    points: [
      { id: 'rear-axle', label: 'Rear axle', hint: 'Centre of the rear hub in the render.', bikeAnchor: 'rearAxle' },
      { id: 'head-top', label: 'Head tube top', hint: 'Centre of the upper headset cup in the render.', bikeAnchor: 'headTubeTop' },
    ],
  },
  forks: {
    kind: 'two-point',
    zIndex: 45,
    points: [
      { id: 'axle', label: 'Axle', hint: 'Centre of the front axle / dropout.', bikeAnchor: 'frontAxle' },
      { id: 'crown', label: 'Crown top', hint: 'Centre of the crown where the steerer enters — the line to the axle is the pull direction.', bikeAnchor: 'headTubeBot' },
    ],
  },
  shocks: {
    kind: 'two-point',
    zIndex: 55,
    points: [
      { id: 'eye-front', label: 'Front eye', hint: 'Centre of the shock eye that mounts to the front of the frame.', bikeAnchor: 'shock-front' },
      { id: 'eye-rear', label: 'Rear eye', hint: 'Centre of the shock eye that mounts to the rocker.', bikeAnchor: 'shock-rear' },
    ],
  },

  // ── Round parts: centre dot + diameter circle ─────────────────────────────
  'wheels/wheelset': {
    kind: 'point-radius',
    zIndex: 10,
    mirror: true,
    radiusSource: 'wheel',
    points: [
      { id: 'hub', label: 'Axle', hint: 'Dead centre of the hub.', bikeAnchor: 'frontAxle' },
      { id: 'radius', label: 'Rim outer edge', hint: 'Drag the circle to the outer edge of the rim, where the tyre bead seats — it meets the selected tyre’s inner diameter.' },
    ],
  },
  'wheels/tires': {
    kind: 'three-edge',
    zIndex: 12,
    mirror: true,
    radiusSource: 'wheel',
    innerRadiusPointId: 'inner-edge',
    points: [
      { id: 'edge-a', label: 'Tread edge 1', hint: 'Click any point on the outer edge of the tyre tread.', bikeAnchor: 'frontAxle' },
      { id: 'edge-b', label: 'Tread edge 2', hint: 'Click a second point on the tread edge, well away from the first.' },
      { id: 'edge-c', label: 'Tread edge 3', hint: 'Click a third point on the tread edge — spread them around the tyre for the best fit.' },
      { id: 'inner-edge', label: 'Rim line', hint: 'Optional: click the inside edge of the tyre where it meets the rim — this is where the rim’s outer edge lands.', optional: true },
    ],
  },
  'brakes/rotor': {
    kind: 'point-radius',
    zIndex: 11,
    mirror: true,
    radiusSource: 'value',
    defaultLengthMm: 200,
    lengthLabel: 'Rotor diameter mm',
    points: [
      { id: 'centre', label: 'Rotor centre', hint: 'Dead centre of the rotor.', bikeAnchor: 'frontAxle' },
      { id: 'radius', label: 'Rotor edge', hint: 'Drag the circle to the outer edge of the rotor.' },
    ],
  },

  // ── Two-mount drivetrain ───────────────────────────────────────────────────
  'drivetrain/groupset': {
    kind: 'two-point',
    zIndex: 60,
    points: [
      { id: 'crank-centre', label: 'Crank spindle', hint: 'Centre of the chainring / crank spindle.', bikeAnchor: 'bb' },
      { id: 'cassette', label: 'Cassette centre', hint: 'Centre of the cassette at the rear hub.', bikeAnchor: 'rearAxle' },
    ],
  },
  'drivetrain/cranks': {
    kind: 'point-length',
    zIndex: 62,
    defaultLengthMm: 170,
    lengthLabel: 'Crank length mm',
    // Match the geometry's static crank angle (CRANK_ANGLE_DEG) so the arm end
    // lands exactly on the pedal-axle anchor (`crank-end`), which pedals snap to.
    defaultAngleDeg: 0,
    points: [
      { id: 'spindle', label: 'Spindle', hint: 'Centre of the crank spindle — snaps to the BB.', bikeAnchor: 'bb' },
      { id: 'arm-end', label: 'Pedal axle', hint: 'Draw the arm-length line: click the pedal thread at the end of the arm.' },
    ],
  },
  'drivetrain/chainring': {
    kind: 'point-radius',
    zIndex: 61,
    radiusSource: 'value',
    defaultLengthMm: 130,
    lengthLabel: 'Chainring diameter mm',
    points: [
      { id: 'centre', label: 'Ring centre', hint: 'Dead centre of the chainring — snaps to the BB / crank spindle.', bikeAnchor: 'bb' },
      { id: 'radius', label: 'Ring edge', hint: 'Drag the circle to the outer edge of the chainring teeth.' },
    ],
  },
  'drivetrain/pedals': {
    kind: 'point-length',
    zIndex: 64,
    defaultLengthMm: 100,
    lengthLabel: 'Pedal body width mm',
    defaultAngleDeg: 0,
    points: [
      { id: 'centre', label: 'Pedal axle', hint: 'Centre of the pedal axle — snaps to the crank end.', bikeAnchor: 'crank-end' },
      { id: 'edge', label: 'Body edge', hint: 'Draw a line across the pedal body to set its real width.' },
    ],
  },
  'drivetrain/bb': {
    kind: 'point-length',
    zIndex: 58,
    defaultLengthMm: 90,
    lengthLabel: 'Shell width mm',
    defaultAngleDeg: 0,
    points: [
      { id: 'centre', label: 'BB centre', hint: 'Centre of the bottom bracket.', bikeAnchor: 'bb' },
      { id: 'edge', label: 'Cup edge', hint: 'Draw a line from the centre to the outer cup edge line.' },
    ],
  },

  // ── Cockpit: mount dot + real-length line ──────────────────────────────────
  'cockpit/combined': {
    kind: 'two-point',
    zIndex: 72,
    points: [
      { id: 'head-top', label: 'Head tube top', hint: 'Centre of the top of the head tube / upper headset cup.', bikeAnchor: 'headTubeTop' },
      { id: 'bar-clamp', label: 'Bar clamp centre', hint: 'Centre of the bar at the stem clamp position.', bikeAnchor: 'bar-clamp' },
    ],
  },
  'cockpit/bars': {
    kind: 'point-length',
    zIndex: 70,
    defaultLengthMm: 250,
    lengthLabel: 'Clamp-to-grip-end mm (side view)',
    defaultAngleDeg: 5,
    points: [
      { id: 'clamp', label: 'Clamp centre', hint: 'Centre of the bar at the stem clamp — snaps to the bar clamp anchor.', bikeAnchor: 'bar-clamp' },
      { id: 'grip-end', label: 'Grip end', hint: 'Draw the line to the end of the grip.' },
    ],
  },
  'cockpit/grips': {
    kind: 'point-length',
    zIndex: 72,
    defaultLengthMm: 135,
    lengthLabel: 'Grip length mm',
    defaultAngleDeg: 0,
    points: [
      { id: 'centre', label: 'Grip centre', hint: 'Middle of the grip — snaps near the bar clamp; nudge it outward per bike.', bikeAnchor: 'bar-clamp' },
      { id: 'end', label: 'Grip end', hint: 'Draw a line along the grip to its end.' },
    ],
  },
  'cockpit/headset': {
    kind: 'point-length',
    zIndex: 68,
    defaultLengthMm: 55,
    lengthLabel: 'Cup diameter mm',
    defaultAngleDeg: 0,
    points: [
      { id: 'centre', label: 'Upper cup', hint: 'Centre of the upper headset cup — snaps to the head tube top.', bikeAnchor: 'headTubeTop' },
      { id: 'edge', label: 'Cup edge', hint: 'Draw a line across the cup to set its real size.' },
    ],
  },
  // Two-point: both stem mounts are known bike-side points, so position, rotation
  // and scale all solve exactly per bike — no guessed angle or typed length.
  'cockpit/stem': {
    kind: 'two-point',
    zIndex: 69,
    points: [
      { id: 'clamp', label: 'Bar clamp', hint: 'Centre of the bar at the faceplate — snaps to the bar clamp anchor.', bikeAnchor: 'bar-clamp' },
      { id: 'steerer', label: 'Steerer clamp', hint: 'Centre of the steerer clamp — snaps to the head tube top.', bikeAnchor: 'headTubeTop' },
    ],
  },
  'cockpit/seat': {
    kind: 'point-length',
    zIndex: 66,
    defaultLengthMm: 245,
    lengthLabel: 'Saddle length mm',
    defaultAngleDeg: 0,
    points: [
      { id: 'rails', label: 'Rail centre', hint: 'Centre of the saddle rails — snaps to the saddle anchor.', bikeAnchor: 'saddle' },
      { id: 'nose', label: 'Nose', hint: 'Draw the line from the rails to the saddle nose.' },
    ],
  },
  // Seat post: pinned at the saddle clamp and run DOWN the actual seat-tube
  // line, so the post always meets the saddle — the junction riders actually
  // look at — on every bike and at every saddle height. Pinning at the frame's
  // seat-tube collar instead (seatTubeTop, at stack height) left the post
  // floating ~150mm past the saddle at any true clamp-to-collar length. The
  // dot ids match the original two-point scheme so posts annotated under it
  // solve again without re-annotating.
  'cockpit/post': {
    kind: 'point-length',
    zIndex: 30,
    defaultLengthMm: 250,
    lengthLabel: 'Clamp to collar mm',
    angleSource: 'actual-seat-tube',
    points: [
      { id: 'head', label: 'Saddle clamp', hint: 'Centre of the saddle clamp at the head of the post — snaps to the saddle.', bikeAnchor: 'saddle' },
      { id: 'collar', label: 'Seat tube collar', hint: 'Draw the shaft line down to where the post enters the seat tube, and enter the real clamp-to-collar length in mm.' },
    ],
  },

  // ── Brakes ─────────────────────────────────────────────────────────────────
  'brakes/brakeset': {
    kind: 'point-length',
    zIndex: 65,
    defaultLengthMm: 90,
    lengthLabel: 'Caliper length mm',
    defaultAngleDeg: 0,
    points: [
      { id: 'caliper', label: 'Caliper', hint: 'Centre of the brake caliper — snaps to the caliper mount.', bikeAnchor: 'front-caliper' },
      { id: 'edge', label: 'Caliper end', hint: 'Draw a line along the caliper body to set its real length.' },
    ],
  },
}

const FALLBACK_SCHEME: AnchorScheme = {
  kind: 'point-length',
  zIndex: 50,
  defaultLengthMm: 200,
  lengthLabel: 'Real length mm',
  defaultAngleDeg: 0,
  points: [
    { id: 'centre', label: 'Mount point', hint: 'Where the part attaches to the bike.', bikeAnchor: 'bb' },
    { id: 'edge', label: 'Reference end', hint: 'Draw a line across a known dimension of the part.' },
  ],
}

/** Look up the connection scheme for a category (and sub-category when it has one). */
export function schemeFor(categoryId: PartCategoryId, subCategoryId?: string | null): AnchorScheme {
  if (subCategoryId) {
    const scoped = SCHEMES[`${categoryId}/${subCategoryId}`]
    if (scoped) return scoped
  }
  return SCHEMES[categoryId] ?? FALLBACK_SCHEME
}

// ── Preview layer stacking ───────────────────────────────────────────────────
//
// Every part image and the frame sit in one front-to-back stack in the builder
// preview. The default order comes from each scheme's `zIndex`, but the admin can
// reorder the whole stack from the Layer order workbench tab — so the resolved
// z-index is looked up from a saved order of layer keys rather than read straight
// off the scheme.

/** The layer key a category/sub-category renders under — the matched scheme key. */
export function layerKeyFor(categoryId: PartCategoryId, subCategoryId?: string | null): string {
  if (subCategoryId && SCHEMES[`${categoryId}/${subCategoryId}`]) return `${categoryId}/${subCategoryId}`
  if (SCHEMES[categoryId]) return categoryId
  return categoryId
}

/** Friendly label for a layer key, e.g. `wheels/tires` → "Wheels · Tyres". */
function labelForLayerKey(key: string): string {
  if (key === 'frame') return 'Frame'
  const [categoryId, subId] = key.split('/')
  const category = PARTS.find((item) => item.id === categoryId)
  const categoryLabel = category?.label ?? categoryId ?? key
  if (!subId) return categoryLabel
  const subLabel = category?.subcategories?.find((sub) => sub.id === subId)?.label ?? subId
  return `${categoryLabel} · ${subLabel}`
}

export interface LayerSlot {
  key: string
  label: string
  defaultZIndex: number
}

/**
 * Canonical list of stackable layer slots, back (lowest z) → front. `frame/render`
 * shares the `frame` slot, so it is folded in rather than listed separately.
 */
export const LAYER_SLOTS: LayerSlot[] = Object.entries(SCHEMES)
  .filter(([key]) => key !== 'frame/render')
  .map(([key, scheme]) => ({ key, label: labelForLayerKey(key), defaultZIndex: scheme.zIndex }))
  .sort((a, b) => a.defaultZIndex - b.defaultZIndex)

/** Default stacking order (layer keys), back → front. */
export const DEFAULT_LAYER_ORDER: string[] = LAYER_SLOTS.map((slot) => slot.key)

/**
 * Resolve a layer key to a CSS z-index from the saved order. Position 0 (back)
 * gets the lowest value; unknown keys float to the front. Spaced by 10 so the
 * frame's two sub-layers (tubes/lugs) can sit next to each other.
 */
export function resolveLayerZIndex(order: string[], key: string): number {
  const index = order.indexOf(key)
  const position = index === -1 ? order.length : index
  return (position + 1) * 10
}

/**
 * Reconcile a stored order with the current slot list: keep known keys in their
 * saved position, drop unknown ones, and append any slots added since (in their
 * default position). Guarantees every current layer is always placed.
 */
export function normaliseLayerOrder(order: string[] | null | undefined): string[] {
  const known = new Set(DEFAULT_LAYER_ORDER)
  const seen = new Set<string>()
  const kept = (order ?? []).filter((key) => known.has(key) && !seen.has(key) && seen.add(key))
  const missing = DEFAULT_LAYER_ORDER.filter((key) => !seen.has(key))
  return [...kept, ...missing]
}
